import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActiveCallPanel = ({ callData, onCallAction, callDuration }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours?.toString()?.padStart(2, '0')}:${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
    }
    return `${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleMute = () => {
    setIsMuted(!isMuted);
    onCallAction('mute', !isMuted);
  };

  const handleHold = () => {
    setIsOnHold(!isOnHold);
    onCallAction('hold', !isOnHold);
  };

  const handleRecord = () => {
    setIsRecording(!isRecording);
    onCallAction('record', !isRecording);
  };

  const handleTransfer = () => {
    onCallAction('transfer');
  };

  const handleEndCall = () => {
    onCallAction('end');
  };

  if (!callData) {
    return (
      <div className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
        <div className="text-center py-8">
          <Icon name="Phone" size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Aktif Arama Yok</h3>
          <p className="text-sm text-slate-600">
            Arama başlatmak için sol paneldeki tuş takımını kullanın
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
      {/* Call Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-3 h-3 bg-success rounded-full pulse-slow" />
          <h3 className="text-lg font-semibold text-slate-900">Aktif Arama</h3>
        </div>
        <div className="text-right">
          <div className="call-timer text-xl font-mono font-semibold text-slate-900">
            {formatDuration(callDuration)}
          </div>
          <div className="text-xs text-slate-500">Arama süresi</div>
        </div>
      </div>
      {/* Customer Information */}
      <div className="bg-slate-50 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
            <Icon name="User" size={24} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-slate-900 mb-1">
              {callData?.customerName}
            </h4>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={14} className="text-slate-500" />
                <span className="text-sm text-slate-600">{callData?.phoneNumber}</span>
              </div>
              {callData?.email && (
                <div className="flex items-center space-x-2">
                  <Icon name="Mail" size={14} className="text-slate-500" />
                  <span className="text-sm text-slate-600">{callData?.email}</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={14} className="text-slate-500" />
                <span className="text-sm text-slate-600">{callData?.location}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
              callData?.callType === 'Gelen' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
            }`}>
              <Icon 
                name={callData?.callType === 'Gelen' ? "ArrowDown" : "ArrowUp"} 
                size={10} 
                className="mr-1" 
              />
              {callData?.callType}
            </span>
            {callData?.priority && (
              <div className="mt-1">
                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                  callData?.priority === 'Yüksek' ? 'bg-red-100 text-red-800' : 
                  callData?.priority === 'Orta'? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {callData?.priority} Öncelik
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Call Status Indicators */}
      {(isMuted || isOnHold || isRecording) && (
        <div className="flex items-center space-x-2 mb-4">
          {isMuted && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
              <Icon name="MicOff" size={12} className="mr-1" />
              Sessize Alındı
            </span>
          )}
          {isOnHold && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
              <Icon name="Pause" size={12} className="mr-1" />
              Beklemede
            </span>
          )}
          {isRecording && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              <Icon name="Circle" size={12} className="mr-1 text-red-500" />
              Kaydediliyor
            </span>
          )}
        </div>
      )}
      {/* Call Controls */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Button
          variant={isMuted ? "destructive" : "outline"}
          size="default"
          onClick={handleMute}
          iconName={isMuted ? "MicOff" : "Mic"}
          iconPosition="left"
        >
          {isMuted ? 'Sesi Aç' : 'Sessize Al'}
        </Button>

        <Button
          variant={isOnHold ? "warning" : "outline"}
          size="default"
          onClick={handleHold}
          iconName={isOnHold ? "Play" : "Pause"}
          iconPosition="left"
        >
          {isOnHold ? 'Devam Et' : 'Beklet'}
        </Button>

        <Button
          variant={isRecording ? "success" : "outline"}
          size="default"
          onClick={handleRecord}
          iconName="Circle"
          iconPosition="left"
        >
          {isRecording ? 'Kaydı Durdur' : 'Kaydet'}
        </Button>

        <Button
          variant="outline"
          size="default"
          onClick={handleTransfer}
          iconName="ArrowRightLeft"
          iconPosition="left"
        >
          Transfer Et
        </Button>
      </div>
      {/* End Call Button */}
      <Button
        variant="destructive"
        size="lg"
        onClick={handleEndCall}
        iconName="PhoneOff"
        iconPosition="left"
        className="w-full"
      >
        Aramayı Sonlandır
      </Button>
    </div>
  );
};

export default ActiveCallPanel;